import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Theme, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import FormSelectField from '../../components/formFields/FormSelectField';
import FormToggleField from '../../components/formFields/FormToggleField';
import FormSubmitButton from '../../components/buttons/FormSubmitButton';
import FormCancelButton from '../../components/buttons/FormCancelButton';
import { AutoSweepConfig, } from './types';
import { updateAutoSweepConfig } from '../../api/bankingApi/bankingApi';
import useSnackBar from '../../hooks/useSnackBar';
import { UnrestrictedAccountShort } from '../../types';
import FormLinkButton from '../../components/buttons/FormLinkButton';
import { timingOptions, dayOfWeekOptions, dayOfWeekValueMap } from './constants';
import { autoSweepConfigFormSchema } from '../../helpers/formSchemas/formSchemas';

const useStyles = makeStyles()((theme: Theme) => ({
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 700,
    fontFamily: 'Poppins',
    color: theme.palette.common.white,
  },
  sectionSubtitle: {
    fontSize: '14px',
    fontFamily: 'Poppins',
    color: theme.palette.secondary.main,
    marginTop: '4px',
  },
  warningSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '12px',
    '@media (max-width: 950px)': {
      width: '100%',
    },
  },
  warningMessageContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '10px',
    gap: '8px',
    background: theme.palette.primary.light,
    width: 'fit-content',
    margin: '8px 0',
    boxSizing: 'border-box',
    borderRadius: '14px',
    border: `1px solid ${theme.palette.warning.main}`,
    wordBreak: 'break-word',
  },

  warningMessageText: {
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '100%',
    color: theme.palette.warning.main,
    margin: '5px',
  },

  retryButtonDefault: {
    height: '30px',
    maxWidth: '70px',
    width: '100%',
    borderRadius: '8px',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: '500',
    textTransform: 'none',
    color: theme.palette.common.white,

    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.08)',
      background: 'rgba(255, 255, 255, 0.08)',
    },

    '@media (max-width: 550px)': {
      maxWidth: '100%',
    },
  },

  saveButton: {
    background: theme.palette.info.main,

    '&:hover': {
      background: theme.palette.info.main,
    },
  },
  formGrid: {
    marginTop: '32px',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '28px 80px',
    maxWidth: '1100px',

    '@media (max-width: 800px)': {
      gridTemplateColumns: '1fr',
    },
  },
}));

type AutoSweepAccountData = {
  id?: number;
  accountType?: 'REV' | 'EXP' | string;
  balance?: string | number;
  accountName?: string;
  accountNumber?: string;
  name?: string;
};

export type AutoSweepFormValues = {
  autoSweepEnabled: boolean;
  destinationAccount: string;
  sweepTiming: 'immediate' | 'scheduled';
  sweepAmount: string;
  sweepDayOfWeek:
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday';
};

const dayOfWeekApiMap: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

interface Props {
  onCancel: () => void;
  onSave: (updatedConfig: AutoSweepConfig) => void;
  autoSweepData?: AutoSweepAccountData | null;
  autoSweepConfig?: AutoSweepConfig | null;
  unrestrictedAccounts?: UnrestrictedAccountShort[] | null;
}

const EditConfigurationView = ({
  onCancel,
  onSave,
  autoSweepData,
  autoSweepConfig,
  unrestrictedAccounts
}: Props) => {
  const { classes } = useStyles();
  const [isConfigSubmitting, setIsConfigSubmitting] = useState(false);
  const { setAlert } = useSnackBar();
  const firmId = localStorage.getItem('firmId');

  const scheduledDayOfWeek = (
    autoSweepConfig?.trigger?.mode === 'SCHEDULED' &&
      typeof autoSweepConfig.trigger.schedule?.dayOfWeek === 'number'
      ? dayOfWeekValueMap[autoSweepConfig.trigger.schedule.dayOfWeek]
      : 'monday'
  ) as AutoSweepFormValues['sweepDayOfWeek'];

  const destinationAccountId = autoSweepConfig?.destinationAccountId
    ? unrestrictedAccounts?.find((acc) => acc.id === autoSweepConfig.destinationAccountId)?.id?.toString()
    : '';

  const formMethods = useForm<AutoSweepFormValues>({
    mode: 'onChange',
    resolver: yupResolver(autoSweepConfigFormSchema),
    defaultValues: {
      autoSweepEnabled: autoSweepConfig?.enabled ?? false,
      destinationAccount: destinationAccountId || '',
      sweepTiming: 'scheduled',
      sweepAmount: 'full',
      sweepDayOfWeek: scheduledDayOfWeek,
    },
  });

  const {
    formState: { isDirty, isValid },
  } = formMethods;

  const sweepTiming = formMethods.watch('sweepTiming');
  const hasDestinationOptions = !!(unrestrictedAccounts && unrestrictedAccounts.length > 0);

  const handleSave = formMethods.handleSubmit((values) => {
    setIsConfigSubmitting(true);

    const existingScheduledTrigger =
      autoSweepConfig?.trigger?.mode === 'SCHEDULED'
        ? autoSweepConfig.trigger
        : null;

    const trigger =
      values.sweepTiming === 'scheduled'
        ? {
          mode: 'SCHEDULED' as const,
          schedule: {
            dayOfWeek: dayOfWeekApiMap[values.sweepDayOfWeek] ?? 1,
            hour: existingScheduledTrigger?.schedule?.hour ?? 14,
            minute: existingScheduledTrigger?.schedule?.minute ?? 30,
            timezone:
              existingScheduledTrigger?.schedule?.timezone || 'America/New_York',
          },
        }
        : {
          mode: 'IMMEDIATE' as const,
        };

    const payload = {
      id: Number(firmId),
      sweepAccountType: autoSweepData?.accountType,
      enabled: values.autoSweepEnabled,
      destinationAccountId: Number(values.destinationAccount),
      trigger,
      amount: {
        type: 'FULL' as const,
      },
      retryDelayMinutes: autoSweepConfig?.retryDelayMinutes,
      maxRetries: autoSweepConfig?.maxRetries,
    };

    updateAutoSweepConfig(payload)
      .then((res) => {
        setAlert((prev) => ({
          ...prev,
          message: 'Auto-sweep configuration updated successfully.',
          type: 'success',
        }));
        onSave && onSave(res?.data);
      })
      .catch((error) => {
        setAlert((prev) => ({
          ...prev,
          message:
            error.response?.data?.message ||
            'Failed to update auto-sweep configuration.',
          type: 'error',
        }));
      })
      .finally(() => {
        setIsConfigSubmitting(false);
      });
  });



  return (
    <FormProvider {...formMethods}>
      <Box mt={3}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Box display="flex" alignItems="center" gap={3}>
              <Typography className={classes.sectionTitle}>
                Enable Auto-Sweep
              </Typography>
              <FormToggleField name="autoSweepEnabled" />
            </Box>
            
            <Typography className={classes.sectionSubtitle}>
              Funds will move automatically when credited to this account.
            </Typography>
          </Box>
        </Box>

        <Box className={classes.formGrid}>
          {!hasDestinationOptions && (
            <Box className={classes.warningMessageContainer} sx={{ gridColumn: '1 / -1' }}>
              <Typography className={classes.warningMessageText}>
                No unrestricted destination accounts found. Please add an unrestricted account on the Banking page to select it here.
              </Typography>

              <Box>
                <FormLinkButton path={'/banking/accounts'} />
              </Box>
            </Box>
          )}

          <FormSelectField
            name="destinationAccount"
            label="Destination account"
            options={unrestrictedAccounts?.map((account) => ({
              id: account.id.toString(),
              label: account.name,
            })) || []}
            required
            disabled={!hasDestinationOptions}
          />

          <FormSelectField
            name="sweepTiming"
            label="Sweep timing"
            required
            options={timingOptions}
          />


          {sweepTiming === 'scheduled' && (
            <FormSelectField
              name="sweepDayOfWeek"
              label="Day of week"
              options={dayOfWeekOptions}
              required
            />
          )}

        </Box>

        <Box
          mt={8}
          display="flex"
          justifyContent="flex-end"
          gap={2}
          alignItems="center"
        >
          <Box width="120px">
            <FormCancelButton onClick={onCancel} />
          </Box>

          <Box width="120px">
            <FormSubmitButton
              disabled={!isDirty || isConfigSubmitting || !hasDestinationOptions || !isValid}
              buttonText="Save"
              onClick={handleSave}
              fontFamily="Poppins"
            />
          </Box>
        </Box>
      </Box>

    </FormProvider>
  );
};

export default EditConfigurationView;